import React, { FC, useRef, useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import PasswordStrengthBar from 'react-password-strength-bar';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useFormContext, Controller, ControllerRenderProps } from 'react-hook-form';
import AsyncCreatableSelect from 'react-select/async-creatable';

import bg from 'date-fns/locale/bg';
import enGB from 'date-fns/locale/en-GB';
import styled from 'styled-components';

import { isFieldRequired } from 'utils/helpers/formValidations';
import Editor from 'components/editor/TextEditor';
import PagePreview from 'components/editor/PagePreview';
import FormFile from './FormFile';
import FormCheck from './FormCheck';
import FormSwitch from './FormSwitch';
import Datepicker from './Datepicker';
import MultiSelect from './MultiSelect';
import SingleSelect from './SingleSelect';
import { IOptionProps, IFormControlProps } from './types';

import 'react-datepicker/dist/react-datepicker.css';

registerLocale('bg', bg);
registerLocale('en', enGB);

const FormControl: FC<IFormControlProps> = ({
  filterDate,
  type,
  checkType,
  as,
  placeholder,
  name,
  min,
  max,
  autoComplete,
  dateFormat,
  isDateRange,
  startDate,
  endDate,
  minDate,
  maxDate,
  showYearPicker,
  showTimeSelect,
  showMonthDropdown,
  showYearDropdown,
  isRequired,
  isDisabled,
  isPassword,
  isConfirmPassword,
  isVerificationCode,
  isEGN,
  isIdCard,
  isNumber,
  isLeaveDaysCount,
  options,
  noOptionsMessage,
  createMessage,
  disableOptions,
  selectOptions,
  loadOptions,
  isClearable,
  isCreatable,
  isSearchable,
  isMulti,
  showTotal,
  allowSelectAll,
  multiple,
  accept,
  isImport,
  onChange,
  uploadUri,
  deleteFileUri,
  onFilesUploaded,
  onFilesDeleted,
  reset,
  setRef,
  disableLocalization,
  hasOptionTooltip,
  yearDropdownItemNumber,
  scrollableYearDropdown,
  dropdownMode,
  maxLength = undefined,
  isFileUploading,
  initialUploadedFiles,
  isAsyncSelect,
  limitOptions,
  disableFilterOption,
  showMonthYearPicker,
  onMenuClose,
  isWholeNumber,
  showTimeSelectOnly,
  timeIntervals,
  rows,
  testId,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const { register, watch, control, getValues } = useFormContext<any>();

  const preventScroll = (e: WheelEvent) => {
    e.preventDefault();
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (input && type === 'number') {
      input.addEventListener('wheel', preventScroll, { passive: false });
      return () => {
        input.removeEventListener('wheel', preventScroll);
      };
    }
  }, [type]);

  // Watch password field, when validate `confirmPassword`
  const password = useRef({});
  if (isConfirmPassword) {
    password.current = watch('password', '');
  }

  if (options && options.length)
    return (
      <>
        {options.map((option: IOptionProps) => (
          <FormCheck
            key={option.id}
            type={checkType}
            option={option}
            isRequired={isRequired}
            disabled={option.disabled}
          />
        ))}
      </>
    );

  if (checkType === 'switch')
    return (
      <FormSwitch
        name={name}
        isDisabled={isDisabled}
      />
    );

  if (type === 'select')
    return (
      <>
        <Controller
          name={name}
          control={control}
          rules={{ required: isFieldRequired(t, isRequired) }}
          render={({ field }: { field: ControllerRenderProps }) => {
            function handleChange(e: any) {
              field.onChange(e);
              if (onChange) onChange(e);
            }

            if (isAsyncSelect) {
              return (
                <AsyncCreatableSelect
                  defaultOptions
                  cacheOptions
                  loadOptions={loadOptions}
                  value={field.value}
                  onChange={handleChange}
                  placeholder="Въведи код или име по НКПД..."
                  formatCreateLabel={(inputValue: string) => `Създай: ${inputValue}`}
                  noOptionsMessage={() => 'Започни да търсиш'}
                  isClearable={isClearable}
                />
              );
            }

            if (isMulti)
              return (
                <MultiSelect
                  placeholder={placeholder}
                  options={selectOptions}
                  noOptionsMessage={noOptionsMessage}
                  createMessage={createMessage}
                  disableOptions={disableOptions}
                  isDisabled={isDisabled}
                  isCreatable={isCreatable}
                  isClearable={isClearable}
                  isSearchable={isSearchable}
                  showTotal={showTotal}
                  allowSelectAll={allowSelectAll}
                  {...field}
                  limitOptions={limitOptions}
                  disableFilterOption={disableFilterOption}
                  onChange={handleChange}
                  onMenuClose={onMenuClose}
                />
              );

            return (
              <SingleSelect
                placeholder={placeholder}
                options={selectOptions}
                noOptionsMessage={noOptionsMessage}
                createMessage={createMessage}
                disableOptions={disableOptions}
                isDisabled={isDisabled}
                isCreatable={isCreatable}
                isClearable={isClearable}
                isSearchable={isSearchable}
                {...field}
                onChange={handleChange}
                hasOptionTooltip={hasOptionTooltip}
                limitOptions={limitOptions}
                disableFilterOption={disableFilterOption}
                testId={testId}
              />
            );
          }}
        />
      </>
    );

  if (type === 'file')
    return (
      <Controller
        name={name}
        control={control}
        rules={{ required: isFieldRequired(t, isRequired) }}
        render={props => {
          return (
            <FormFile
              placeholder={placeholder}
              multiple={multiple}
              accept={accept}
              isImport={isImport}
              uploadUri={uploadUri}
              deleteFileUri={deleteFileUri}
              onFilesUploaded={onFilesUploaded}
              onFilesDeleted={onFilesDeleted}
              reset={reset}
              props={props.field}
              isFileUploading={isFileUploading}
              initialUploadedFiles={initialUploadedFiles}
            />
          );
        }}
      />
    );

  if (type === 'datepicker') {
    return (
      <Controller
        name={name}
        control={control}
        rules={{ required: isFieldRequired(t, isRequired) }}
        render={props => (
          <Datepicker
            wrapperClassName="d-block"
            className="form-control"
            placeholder={placeholder}
            filterDate={filterDate}
            onChange={(e: any) => {
              props.field.onChange(e);
              if (onChange) onChange(e);
            }}
            showMonthYearPicker={showMonthYearPicker}
            selected={props.field.value}
            isRequired={isRequired}
            readOnly={isDisabled}
            dateFormat={dateFormat}
            showTimeSelect={showTimeSelect}
            showYearPicker={showYearPicker}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={maxDate}
            isDateRange={isDateRange}
            yearDropdownItemNumber={yearDropdownItemNumber}
            scrollableYearDropdown={scrollableYearDropdown}
            dropdownMode={dropdownMode}
            showTimeSelectOnly={showTimeSelectOnly}
            timeIntervals={timeIntervals}
            testId={testId}
          />
        )}
      />
    );
  }

  if (type === 'wysiwyg-editor') {
    return (
      <Controller
        name={name}
        control={control}
        rules={{ required: isFieldRequired(t, isRequired) }}
        render={props => {
          if (isDisabled) {
            return (
              <PagePreview
                content={props.field.value}
                isCentered={false}
              />
            );
          }

          return (
            <Editor
              name={props.field.name}
              content={props.field.value}
              updateContent={(e: any) => props.field.onChange(e)}
              disableLocalization={disableLocalization}
            />
          );
        }}
      />
    );
  }

  if (type === 'number' && onChange && isNumber) {
    return (
      <Controller
        name={name}
        control={control}
        rules={{
          required: isFieldRequired(t, isRequired),
          validate: (value: any): any => {
            let isValid = true;
            const numericValue = Number(value);
            const isNumberValid = !Number.isNaN(numericValue);
            if (!isNumberValid) {
              return t('form.invalidValue');
            }
            if (min !== undefined && !Number.isNaN(min)) {
              isValid = isValid && numericValue >= min;
            }
            if (max !== undefined && !Number.isNaN(max)) {
              isValid = isValid && numericValue <= max;
            }
            if (isWholeNumber) {
              isValid = isValid && numericValue % 1 === 0;
              if (numericValue % 1 !== 0) {
                return t('form.invalidWholeNumber');
              }
            }
            return isValid || t('form.invalidValue');
          },
        }}
        render={({ field }) => (
          <Form.Control
            type="number"
            placeholder={placeholder}
            min={min}
            max={max}
            readOnly={isDisabled}
            value={field.value || ''}
            onChange={e => {
              const newValue = e.target.value.replace(',', '.');
              field.onChange(newValue);
              if (onChange) onChange(e);
            }}
            ref={(e: HTMLInputElement) => {
              inputRef.current = e;
              if (e && type === 'number') {
                e.addEventListener('wheel', preventScroll, { passive: false });
              }
            }}
            maxLength={maxLength}
          />
        )}
      />
    );
  }

  const { ref, ...regProps } = register(name, {
    required: isFieldRequired(t, isRequired),
    ...(type === 'email' && {
      pattern: {
        value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,24}$/,
        message: t('form.invalidEmail'),
      },
    }),
    ...(type === 'number' && {
      validate: (value: number): any => {
        let isValid = true;
        if (!Number.isNaN(min) && min !== undefined) {
          isValid = isValid && value >= min;
        }
        if (!Number.isNaN(max) && max !== undefined) {
          isValid = isValid && value <= max;
        }
        if (isWholeNumber) {
          isValid = isValid && value % 1 === 0;

          if (value % 1 !== 0) {
            return t('form.invalidWholeNumber');
          }
        }
        return isValid || t('form.invalidValue');
      },
    }),
    ...(isNumber && {
      validate: (value: any): any => {
        let isValid = true;
        // Check if value is a valid number (applies to both cases)
        const numericValue = Number(value);
        const isNumberValid = !Number.isNaN(numericValue);

        if (!isNumberValid) {
          return t('form.invalidValue');
        }

        // Validate min/max (applies to both cases)
        if (min !== undefined && !Number.isNaN(min)) {
          isValid = isValid && numericValue >= min;
        }
        if (max !== undefined && !Number.isNaN(max)) {
          isValid = isValid && numericValue <= max;
        }

        // Additional check for whole numbers
        if (isWholeNumber) {
          isValid = isValid && numericValue % 1 === 0;
          if (numericValue % 1 !== 0) {
            return t('form.invalidWholeNumber');
          }
        }

        return isValid || t('form.invalidValue');
      },
      setValueAs: (value: any) => String(value).replace(',', '.'),
    }),
    ...(isLeaveDaysCount && {
      validate: (value: string): any => {
        let isValidNumber = !Number.isNaN(Number(value));
        if (value === '' && isRequired) return t('form.isRequired');
        if (isValidNumber) {
          if (Number.isInteger(Number(min)))
            // @ts-ignore
            isValidNumber = isValidNumber && Number(value) >= min;
          if (Number.isInteger(Number(max)))
            // @ts-ignore
            isValidNumber = isValidNumber && Number(value) <= max;
        }
        return isValidNumber || t('form.invalidValue');
      },
    }),
    ...(isConfirmPassword && {
      validate: (value: string): any => value === password.current || t('form.passMismatch'),
    }),
    ...(isVerificationCode && {
      pattern: {
        value: /^[0-9]{6}$/,
        message: t('form.invalidFormat'),
      },
    }),
    ...(isEGN && {
      pattern: {
        value: /^[0-9]{10}$/,
        message: t('form.invalidFormat'),
      },
    }),
    ...(isIdCard && {
      pattern: {
        value: /^(?:[0-9]{9}|[A-Z]{2}[0-9]{7})$/,
        message: t('form.invalidFormat'),
      },
    }),
  });

  const renderFormControl = () => (
    <Form.Control
      type={isPassword && showPassword ? 'text' : type}
      as={as}
      rows={as === 'textarea' ? rows || 5 : null}
      placeholder={placeholder}
      autoComplete={autoComplete}
      min={min}
      max={max}
      readOnly={isDisabled}
      ref={(e: HTMLInputElement) => {
        inputRef.current = e;
        if (typeof ref === 'function') ref(e);
        if (setRef) setRef(e);
      }}
      maxLength={maxLength}
      {...regProps}
    />
  );
  return (
    <>
      {
        /**
         * If the field is password field, wrap it with InputGroup
         * to show the "show password" button next to password input
         */
        isPassword ? (
          <InputGroup>
            {renderFormControl()}
            <InputGroupAppend onClick={() => setShowPassword(!showPassword)}>
              <InputGroup.Text>{showPassword ? <FaEyeSlash /> : <FaEye />}</InputGroup.Text>
            </InputGroupAppend>
          </InputGroup>
        ) : (
          renderFormControl()
        )
      }
      {/*
        Show password strength bar if field:
        - is of type `password`, BUT
        - is not `password_confirmation` or `old_password`.
      */}
      {isPassword && name !== 'password_confirmation' && name !== 'old_password' && (
        <PasswordStrengthBar
          password={getValues('password')}
          minLength={10}
          shortScoreWord={t('form.tooShort')}
          scoreWords={[t('form.weak'), t('form.weak'), t('form.okay'), t('form.good'), t('form.strong')]}
          barColors={['#ddd', '#ef476f', '#FFB822', '#2b90ef', '#32BFA3']}
          className="c-pass-strength-bar"
          scoreWordClassName="c-score-word"
        />
      )}
    </>
  );
};

export default FormControl;

const InputGroupAppend = styled(InputGroup.Append)`
  cursor: pointer;
`;
